import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { LogOut, Save, Plus, Trash2, Loader2, Lock, ArrowLeft, Home } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { ImageUploader } from "@/components/admin/ImageUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const clone = (o) => JSON.parse(JSON.stringify(o));
const tid = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const LoginView = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await login(email, password);
    setBusy(false);
    if (!res.ok) setError(res.error);
  };

  return (
    <div className="min-h-screen bg-charcoal flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md bg-cream rounded-3xl p-9 shadow-2xl"
      >
        <div className="flex flex-col items-center text-center">
          <Logo />
          <div className="mt-6 w-12 h-12 rounded-full bg-burgundy/10 flex items-center justify-center">
            <Lock className="text-burgundy" size={22} />
          </div>
          <h1 className="mt-4 font-heading text-2xl font-semibold text-charcoal">Owner Login</h1>
          <p className="mt-1 font-body text-sm text-charcoal/60">
            Sign in to update your menu, prices &amp; photos.
          </p>
        </div>

        <form onSubmit={submit} className="mt-7 space-y-4">
          <div>
            <Label htmlFor="email" className="font-body text-charcoal/80">Email</Label>
            <Input
              id="email"
              type="email"
              data-testid="login-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="chefrainey@yahoo.com"
              className="mt-1.5"
              required
            />
          </div>
          <div>
            <Label htmlFor="password" className="font-body text-charcoal/80">Password</Label>
            <Input
              id="password"
              type="password"
              data-testid="login-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-1.5"
              required
            />
          </div>
          {error && (
            <p data-testid="login-error" className="font-body text-sm text-red-600">{error}</p>
          )}
          <Button
            type="submit"
            data-testid="login-submit-btn"
            disabled={busy}
            className="w-full bg-burgundy hover:bg-burgundy-light text-cream rounded-full py-6 font-body font-semibold"
          >
            {busy ? <Loader2 className="animate-spin" size={18} /> : "Sign In"}
          </Button>
        </form>
        <button
          onClick={() => navigate("/")}
          data-testid="login-back-home"
          className="mt-5 w-full flex items-center justify-center gap-2 font-body text-sm text-charcoal/50 hover:text-burgundy transition-colors"
        >
          <ArrowLeft size={15} /> Back to website
        </button>
      </motion.div>
    </div>
  );
};

const PriceRow = ({ item, onName, onPrice, onRemove, prefix }) => (
  <div className="flex items-center gap-2" data-testid={`${prefix}-row-${tid(item.name)}`}>
    <Input
      value={item.name}
      onChange={(e) => onName(e.target.value)}
      className="flex-1"
      placeholder="Item name"
    />
    <Input
      value={item.price}
      onChange={(e) => onPrice(e.target.value)}
      className="w-28"
      placeholder="$0.00"
    />
    <Button type="button" variant="ghost" size="icon" onClick={onRemove} className="text-red-500 hover:text-red-700 shrink-0">
      <Trash2 size={17} />
    </Button>
  </div>
);

export const Admin = () => {
  const { user, checking, logout } = useAuth();
  const { content, loading, refresh } = useContent();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (content && !loading) setDraft(clone(content));
  }, [content, loading]);

  if (checking) {
    return (
      <div className="min-h-screen bg-charcoal flex items-center justify-center">
        <Loader2 className="animate-spin text-gold" size={32} />
      </div>
    );
  }

  if (!user) return <LoginView />;

  if (!draft) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Loader2 className="animate-spin text-burgundy" size={32} />
      </div>
    );
  }

  const setImages = (key, url) => setDraft((d) => ({ ...d, images: { ...d.images, [key]: url } }));

  const updateDish = (i, patch) =>
    setDraft((d) => {
      const dishes = clone(d.dishes);
      dishes[i] = { ...dishes[i], ...patch };
      return { ...d, dishes };
    });
  const addDish = () =>
    setDraft((d) => ({ ...d, dishes: [...d.dishes, { name: "New Dish", desc: "", img: "" }] }));
  const removeDish = (i) =>
    setDraft((d) => ({ ...d, dishes: d.dishes.filter((_, idx) => idx !== i) }));

  const updateMenuItem = (ci, ii, patch) =>
    setDraft((d) => {
      const menu = clone(d.menu);
      menu[ci].items[ii] = { ...menu[ci].items[ii], ...patch };
      return { ...d, menu };
    });
  const addMenuItem = (ci) =>
    setDraft((d) => {
      const menu = clone(d.menu);
      menu[ci].items.push({ name: "New Item", price: "$0.00" });
      return { ...d, menu };
    });
  const removeMenuItem = (ci, ii) =>
    setDraft((d) => {
      const menu = clone(d.menu);
      menu[ci].items = menu[ci].items.filter((_, idx) => idx !== ii);
      return { ...d, menu };
    });

  const updateLunchItem = (ii, patch) =>
    setDraft((d) => {
      const ls = clone(d.lunchSpecial);
      ls.items[ii] = { ...ls.items[ii], ...patch };
      return { ...d, lunchSpecial: ls };
    });
  const addLunchItem = () =>
    setDraft((d) => ({ ...d, lunchSpecial: { ...d.lunchSpecial, items: [...d.lunchSpecial.items, { name: "New Item", price: "$0.00" }] } }));
  const removeLunchItem = (ii) =>
    setDraft((d) => ({ ...d, lunchSpecial: { ...d.lunchSpecial, items: d.lunchSpecial.items.filter((_, idx) => idx !== ii) } }));

  const updateSide = (i, patch) =>
    setDraft((d) => {
      const sides = clone(d.sides);
      sides[i] = { ...sides[i], ...patch };
      return { ...d, sides };
    });
  const addSide = () => setDraft((d) => ({ ...d, sides: [...d.sides, { name: "New Side", small: "$0", large: "$0" }] }));
  const removeSide = (i) => setDraft((d) => ({ ...d, sides: d.sides.filter((_, idx) => idx !== i) }));

  const updateDrink = (i, patch) =>
    setDraft((d) => {
      const drinks = clone(d.drinks);
      drinks[i] = { ...drinks[i], ...patch };
      return { ...d, drinks };
    });
  const addDrink = () => setDraft((d) => ({ ...d, drinks: [...d.drinks, { name: "New Drink", price: "$0.00" }] }));
  const removeDrink = (i) => setDraft((d) => ({ ...d, drinks: d.drinks.filter((_, idx) => idx !== i) }));

  const catering = draft.catering || [];
  const updateCatering = (i, patch) =>
    setDraft((d) => {
      const cat = clone(d.catering || []);
      cat[i] = { ...cat[i], ...patch };
      return { ...d, catering: cat };
    });
  const addCatering = () =>
    setDraft((d) => ({ ...d, catering: [...(d.catering || []), { img: "", caption: "" }] }));
  const removeCatering = (i) =>
    setDraft((d) => ({ ...d, catering: (d.catering || []).filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", draft);
      await refresh();
      toast.success("Changes saved & live on your website!");
    } catch (e) {
      toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-beige pb-28">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-charcoal text-cream shadow-lg">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo variant="light" />
            <span className="hidden sm:inline font-body text-xs uppercase tracking-[0.25em] text-gold">
              Owner Dashboard
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              data-testid="admin-view-site-btn"
              className="text-cream/80 hover:text-gold hover:bg-white/5 font-body text-sm gap-2"
            >
              <Home size={16} /> <span className="hidden sm:inline">View Site</span>
            </Button>
            <Button
              variant="ghost"
              onClick={logout}
              data-testid="admin-logout-btn"
              className="text-cream/80 hover:text-red-300 hover:bg-white/5 font-body text-sm gap-2"
            >
              <LogOut size={16} /> <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 md:px-8 pt-8">
        <h1 className="font-heading text-3xl font-semibold text-charcoal mb-1">
          Welcome, <span className="font-script text-burgundy text-4xl">Chef Rainey</span>
        </h1>
        <p className="font-body text-charcoal/60 mb-8">
          Update prices and photos anytime — changes go live instantly.
        </p>

        <Tabs defaultValue="images" className="w-full">
          <TabsList className="bg-white border border-gold/30 rounded-full p-1 mb-8 flex-wrap h-auto">
            <TabsTrigger value="images" data-testid="tab-images" className="rounded-full data-[state=active]:bg-burgundy data-[state=active]:text-cream font-body">
              Featured Photos
            </TabsTrigger>
            <TabsTrigger value="dishes" data-testid="tab-dishes" className="rounded-full data-[state=active]:bg-burgundy data-[state=active]:text-cream font-body">
              Featured Dishes
            </TabsTrigger>
            <TabsTrigger value="menu" data-testid="tab-menu" className="rounded-full data-[state=active]:bg-burgundy data-[state=active]:text-cream font-body">
              Menu &amp; Prices
            </TabsTrigger>
            <TabsTrigger value="catering" data-testid="tab-catering" className="rounded-full data-[state=active]:bg-burgundy data-[state=active]:text-cream font-body">
              Catering Photos
            </TabsTrigger>
          </TabsList>

          {/* IMAGES */}
          <TabsContent value="images" className="space-y-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 bg-white rounded-3xl p-7 border border-beige">
              <ImageUploader
                label="Homepage Hero Photo"
                testid="hero-image"
                aspect="aspect-[4/3]"
                value={draft.images.hero}
                onChange={(url) => setImages("hero", url)}
              />
              <ImageUploader
                label="Chef Rainey (About) Photo"
                testid="owner-image"
                aspect="aspect-[4/3]"
                value={draft.images.owner}
                onChange={(url) => setImages("owner", url)}
              />
              <ImageUploader
                label="Quinton Rainey — Co-Owner Photo"
                testid="coowner-image"
                aspect="aspect-[4/3]"
                value={draft.images.coowner}
                onChange={(url) => setImages("coowner", url)}
              />
            </div>
          </TabsContent>

          {/* DISHES */}
          <TabsContent value="dishes" className="space-y-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {draft.dishes.map((dish, i) => (
                <div key={i} data-testid={`dish-editor-${i}`} className="bg-white rounded-3xl p-5 border border-beige space-y-3">
                  <ImageUploader
                    testid={`dish-${i}`}
                    aspect="aspect-[4/3]"
                    value={dish.img}
                    onChange={(url) => updateDish(i, { img: url })}
                  />
                  <Input
                    value={dish.name}
                    data-testid={`dish-name-${i}`}
                    onChange={(e) => updateDish(i, { name: e.target.value })}
                    placeholder="Dish name"
                    className="font-heading font-semibold"
                  />
                  <Textarea
                    value={dish.desc}
                    data-testid={`dish-desc-${i}`}
                    onChange={(e) => updateDish(i, { desc: e.target.value })}
                    placeholder="Short description"
                    rows={2}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    data-testid={`dish-remove-${i}`}
                    onClick={() => removeDish(i)}
                    className="w-full text-red-500 border-red-200 hover:bg-red-50 gap-2"
                  >
                    <Trash2 size={16} /> Remove
                  </Button>
                </div>
              ))}
            </div>
            <Button type="button" onClick={addDish} data-testid="add-dish-btn" className="bg-gold text-charcoal hover:bg-gold-light rounded-full gap-2">
              <Plus size={18} /> Add Featured Dish
            </Button>
          </TabsContent>

          {/* MENU */}
          <TabsContent value="menu" className="space-y-6">
            {/* Categories */}
            {draft.menu.map((cat, ci) => (
              <div key={ci} className="bg-white rounded-3xl p-6 border border-beige">
                <h3 className="font-heading text-xl font-semibold text-burgundy mb-4">{cat.category}</h3>
                <div className="space-y-2">
                  {cat.items.map((item, ii) => (
                    <PriceRow
                      key={ii}
                      item={item}
                      prefix={tid(cat.category)}
                      onName={(v) => updateMenuItem(ci, ii, { name: v })}
                      onPrice={(v) => updateMenuItem(ci, ii, { price: v })}
                      onRemove={() => removeMenuItem(ci, ii)}
                    />
                  ))}
                </div>
                <Button type="button" variant="ghost" onClick={() => addMenuItem(ci)} data-testid={`add-item-${tid(cat.category)}`} className="mt-3 text-burgundy hover:bg-burgundy/5 gap-2">
                  <Plus size={16} /> Add item
                </Button>
              </div>
            ))}

            {/* Lunch Special */}
            <div className="bg-white rounded-3xl p-6 border border-beige">
              <h3 className="font-heading text-xl font-semibold text-burgundy mb-4">Lunch Special</h3>
              <div className="space-y-2">
                {draft.lunchSpecial.items.map((item, ii) => (
                  <PriceRow
                    key={ii}
                    item={item}
                    prefix="lunch"
                    onName={(v) => updateLunchItem(ii, { name: v })}
                    onPrice={(v) => updateLunchItem(ii, { price: v })}
                    onRemove={() => removeLunchItem(ii)}
                  />
                ))}
              </div>
              <Button type="button" variant="ghost" onClick={addLunchItem} data-testid="add-item-lunch" className="mt-3 text-burgundy hover:bg-burgundy/5 gap-2">
                <Plus size={16} /> Add item
              </Button>
            </div>

            {/* Sides */}
            <div className="bg-white rounded-3xl p-6 border border-beige">
              <h3 className="font-heading text-xl font-semibold text-burgundy mb-4">Sides</h3>
              <div className="space-y-2">
                {draft.sides.map((s, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`side-row-${tid(s.name)}`}>
                    <Input value={s.name} onChange={(e) => updateSide(i, { name: e.target.value })} className="flex-1" placeholder="Side name" />
                    <Input value={s.small} onChange={(e) => updateSide(i, { small: e.target.value })} className="w-20" placeholder="Small" />
                    <Input value={s.large} onChange={(e) => updateSide(i, { large: e.target.value })} className="w-20" placeholder="Large" />
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeSide(i)} className="text-red-500 hover:text-red-700 shrink-0">
                      <Trash2 size={17} />
                    </Button>
                  </div>
                ))}
              </div>
              <Button type="button" variant="ghost" onClick={addSide} data-testid="add-side-btn" className="mt-3 text-burgundy hover:bg-burgundy/5 gap-2">
                <Plus size={16} /> Add side
              </Button>
            </div>

            {/* Drinks */}
            <div className="bg-white rounded-3xl p-6 border border-beige">
              <h3 className="font-heading text-xl font-semibold text-burgundy mb-4">Drinks</h3>
              <div className="space-y-2">
                {draft.drinks.map((item, i) => (
                  <PriceRow
                    key={i}
                    item={item}
                    prefix="drink"
                    onName={(v) => updateDrink(i, { name: v })}
                    onPrice={(v) => updateDrink(i, { price: v })}
                    onRemove={() => removeDrink(i)}
                  />
                ))}
              </div>
              <Button type="button" variant="ghost" onClick={addDrink} data-testid="add-drink-btn" className="mt-3 text-burgundy hover:bg-burgundy/5 gap-2">
                <Plus size={16} /> Add drink
              </Button>
            </div>
          </TabsContent>

          {/* CATERING PHOTOS */}
          <TabsContent value="catering" className="space-y-6">
            <p className="font-body text-charcoal/60">
              Upload photos of your catering spreads &amp; events. Add a short caption for each (optional).
              These appear in the Catering section on your website.
            </p>
            {catering.length === 0 && (
              <div data-testid="catering-empty" className="bg-white rounded-3xl p-10 border border-dashed border-gold/40 text-center text-charcoal/50 font-body">
                No catering photos yet. Add your first one below.
              </div>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {catering.map((photo, i) => (
                <div key={i} data-testid={`catering-editor-${i}`} className="bg-white rounded-3xl p-5 border border-beige space-y-3">
                  <ImageUploader
                    testid={`catering-${i}`}
                    aspect="aspect-[4/3]"
                    value={photo.img}
                    onChange={(url) => updateCatering(i, { img: url })}
                  />
                  <Input
                    value={photo.caption || ""}
                    data-testid={`catering-caption-${i}`}
                    onChange={(e) => updateCatering(i, { caption: e.target.value })}
                    placeholder="Caption (optional)"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    data-testid={`catering-remove-${i}`}
                    onClick={() => removeCatering(i)}
                    className="w-full text-red-500 border-red-200 hover:bg-red-50 gap-2"
                  >
                    <Trash2 size={16} /> Remove
                  </Button>
                </div>
              ))}
            </div>
            <Button type="button" onClick={addCatering} data-testid="add-catering-btn" className="bg-gold text-charcoal hover:bg-gold-light rounded-full gap-2">
              <Plus size={18} /> Add Catering Photo
            </Button>
          </TabsContent>
        </Tabs>
      </main>

      {/* Sticky save bar */}
      <div className="fixed bottom-0 inset-x-0 z-30 bg-white/90 backdrop-blur-md border-t border-gold/30 py-4">
        <div className="max-w-6xl mx-auto px-5 md:px-8 flex justify-end">
          <Button
            onClick={save}
            data-testid="save-changes-btn"
            disabled={saving}
            className="bg-burgundy hover:bg-burgundy-light text-cream rounded-full px-8 py-6 font-body font-semibold gap-2 shadow-lg"
          >
            {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Admin;
